import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useCallback } from "react";
import Layout from '../../Layout'
import ConnectModal from './ConnectModal'
import './styles.scss'
import ScrollTabs from "./Tab";
import {
    Input,
    Button,
    Badge,
    Avatar,
    Skeleton
} from "antd";
import {
    ReloadOutlined,
    PlusOutlined,
    AppstoreOutlined,
    SearchOutlined,
    TeamOutlined
} from "@ant-design/icons";
import ConnectGroupModal from "./ConnectGroupModal";
import { useNavigate } from "react-router-dom";
import { getChannelConnections, ChannelConnectionItem } from "../../api/ChannelConnection";
import { getChannelGroups, getChannelGroupById } from "../../api/ChannelGroup";
import { CHANNEL_TYPE_LABELS, getChannelImage } from "./channelTypes";

interface GroupTab {
    id: number;
    name: string;
    connectionIds: number[];
}

export default function DashboardPage() {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState("all");
    const [openModal, setOpenModal] = useState(false);
    const [openModalGroup, setOpenModalGroup] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [connections, setConnections] = useState<ChannelConnectionItem[]>([]);
    const [channelGroups, setChannelGroups] = useState<GroupTab[]>([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const fetchConnections = useCallback(async (search?: string, channelTypeCode?: string) => {
        setLoading(true);
        const res = await getChannelConnections({
            search: search || undefined,
            channel_type_code: channelTypeCode || undefined,
            page: 1,
            limit: 200,
        });
        if (res.status && res.data) {
            // Sắp xếp kênh có nhiều khách hàng lên trước cho dễ theo dõi
            const sorted = [...res.data.items].sort(
                (a, b) => (b.total_channel_customers || 0) - (a.total_channel_customers || 0)
            );
            setConnections(sorted);
        }
        setLoading(false);
    }, []);

    // Load nhóm kênh để nối tiếp vào thanh tab; cần connectionIds của từng nhóm
    // để lọc card, nếu API list không trả kèm connections thì gọi chi tiết
    const fetchGroups = useCallback(async () => {
        const res = await getChannelGroups({ limit: 100 });
        if (!res.status) return;
        const groups = await Promise.all(
            res.items.map(async (g): Promise<GroupTab> => {
                if (Array.isArray(g.connections)) {
                    return { id: g.id, name: g.name, connectionIds: g.connections.map(c => c.id) };
                }
                const detail = await getChannelGroupById(g.id);
                return {
                    id: g.id,
                    name: g.name,
                    connectionIds: (detail.data?.connections || []).map(c => c.id),
                };
            })
        );
        setChannelGroups(groups);
    }, []);

    useEffect(() => {
        fetchConnections();
        fetchGroups();
    }, [fetchConnections, fetchGroups]);

    // Build tabs from connections data
    const channelCounts = connections.reduce<Record<string, number>>((acc, item) => {
        acc[item.channel_type_code] = (acc[item.channel_type_code] || 0) + 1;
        return acc;
    }, {});

    const tabs = [
        { key: "all", name: t("chats.tab_all"), count: connections.length, img: "https://pancake.vn/static/images/platform_all.jpg" },
        // Nhóm kênh (chế độ gộp trang) đưa lên đầu, ngay sau "Tất cả" để dễ đọc, không bị che khuất ở cuối
        ...channelGroups.map((g) => ({
            key: `group-${g.id}`,
            name: g.name,
            count: g.connectionIds.length,
            icon: <TeamOutlined />,
        })),
        ...Object.entries(channelCounts).map(([code, count]) => ({
            key: code,
            name: CHANNEL_TYPE_LABELS[code] || code,
            count,
            img: getChannelImage(code),
        })),
    ];

    // Filter connections by active tab
    const filteredConnections = activeTab === "all"
        ? connections
        : connections.filter(c => c.channel_type_code === activeTab);

    const activeChannelType = activeTab === "all" ? undefined : activeTab;

    const handleTabChange = (tabKey: string) => {
        // Tab nhóm kênh: vào thẳng phòng chat gộp trang của nhóm
        if (tabKey.startsWith("group-")) {
            const groupId = tabKey.replace("group-", "");
            navigate(`/chat/${groupId}?group=true`);
            return;
        }
        setActiveTab(tabKey);
    };

    const handleSearch = (value: string) => {
        setSearchValue(value);
        fetchConnections(value, activeChannelType);
    };

    const handleReload = () => {
        fetchConnections(searchValue, activeChannelType);
        fetchGroups();
    };

    return (
        <Layout>
            <Helmet>
                <title>{t('page.chats')} | Khothi</title>
            </Helmet>
            <div>
                {/* HEADER */}
                <div
                    className="
                        rounded-xl
                        p-4 
                        t-bg-white
                        border border-gray-200 
                        transition-colors duration-300
                        mb-4
                    "
                >
                    <div className="flex flex-col items-start lg:flex-row lg:justify-between lg:items-center gap-4">
                        <div>
                            <h2 className="text-lg font-semibold 
                                text-gray-800 
                                dark:text-gray-100"
                            >
                                {t('chats.dashboard')}
                            </h2>
                            {/* SEARCH */}
                            <div className="mt-2 w-[300px]">
                                <Input
                                    prefix={<SearchOutlined />}
                                    placeholder={t('chats.search')}
                                    value={searchValue}
                                    onChange={(e) => handleSearch(e.target.value)}
                                    className="
                                        t-input
                                        bg-gray-100
                                        h-[32px]
                                    "
                                />
                            </div>
                        </div>

                        <div className="flex gap-2">

                            <Button
                                icon={<ReloadOutlined />}
                                onClick={handleReload}
                                className="
                                    bg-gray-100 
                                    dark:bg-gray-700
                                    t-text-white 
                                    border-none
                                    dark:!text-[#357f96]
                                "
                            />

                            <Button
                                icon={<PlusOutlined />}
                                onClick={() => setOpenModal(true)}
                                className="
                                    bg-[#3e91ab] 
                                    hover:!bg-[#357f96]
                                    hover:!text-white
                                    text-white 
                                    border-none
                                "
                            >
                                {t('chats.connect')}
                            </Button>

                            <ConnectModal open={openModal} onClose={() => setOpenModal(false)} />

                            <Button
                                icon={<AppstoreOutlined />}
                                onClick={() => setOpenModalGroup(true)}
                                className="
                                    bg-gray-100 
                                    dark:bg-gray-700 
                                    dark:text-[#357f96] 
                                    border-none
                                    hover:!text-white
                                    hover:!bg-[#3e91ab]
                                "
                            >
                                {t('chats.merge_page')}
                            </Button>
                            <ConnectGroupModal
                                open={openModalGroup}
                                onClose={() => {
                                    setOpenModalGroup(false);
                                    fetchGroups();
                                }}
                            />

                        </div>
                    </div>
                </div>

                {/* TABS */}
                <ScrollTabs
                    tabs={tabs}
                    activeTab={activeTab}
                    onChange={handleTabChange}
                />

                {/* CARDS */}
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">

                    {loading
                        ? Array.from({ length: 8 }).map((_, idx) => (
                            <div
                                key={`skeleton-${idx}`}
                                className="
                                    p-4
                                    rounded-xl
                                    bg-white
                                    dark:bg-[#1f2937]
                                    border border-gray-200
                                    dark:border-gray-700
                                "
                            >
                                <div className="flex items-center gap-3">
                                    <Skeleton.Avatar active size={64} shape="square" />
                                    <div className="flex-1">
                                        <Skeleton active title={false} paragraph={{ rows: 3, width: ['80%', '60%', '50%'] }} />
                                    </div>
                                </div>
                            </div>
                        ))
                        : filteredConnections.map((conn) => (
                            <div
                                key={conn.id}
                                className="
                                group relative
                                p-4
                                rounded-xl
                                bg-white
                                dark:bg-[#1f2937]
                                border border-gray-200
                                dark:border-gray-700
                                hover:shadow-md
                                transition-all duration-300
                                cursor-pointer
                            "
                                onClick={() => navigate(`/chat/${conn.id}`)}
                            >
                                {/* ACTION BUTTONS - tạm ẩn 2 công cụ (sẽ mở lại sau) */}
                                <div className="hidden">
                                    <div className="text-[#c7c9d9] hover:text-[#ff0000]">
                                        <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><circle cx="10" cy="10.002" r="10" fill="currentColor"></circle><path d="M15.3701 15.1652C15.4152 15.2136 15.4502 15.2705 15.4732 15.3325C15.4961 15.3946 15.5065 15.4606 15.5037 15.5267C15.501 15.5928 15.4851 15.6577 15.4571 15.7177C15.429 15.7776 15.3894 15.8314 15.3404 15.8759C15.2915 15.9204 15.2342 15.9547 15.1718 15.9769C15.1095 15.9991 15.0434 16.0087 14.9773 16.0051C14.9112 16.0016 14.8465 15.9849 14.7869 15.9562C14.7273 15.9274 14.674 15.8871 14.6301 15.8377L12.232 13.2002L8.36575 17.3427C8.29157 17.4218 8.19365 17.4747 8.08678 17.4933C7.9799 17.512 7.86986 17.4953 7.77327 17.446C7.67667 17.3966 7.59876 17.3171 7.5513 17.2195C7.50383 17.122 7.48938 17.0116 7.51013 16.9052L8.42638 12.322L4.8245 10.9695C4.74715 10.9406 4.67816 10.8929 4.62371 10.8308C4.56927 10.7687 4.53105 10.6941 4.51248 10.6136C4.49391 10.5332 4.49556 10.4493 4.51729 10.3697C4.53902 10.29 4.58015 10.2169 4.637 10.157L7.08575 7.53203L4.63013 4.83765C4.58504 4.78922 4.55002 4.73233 4.52709 4.67026C4.50415 4.6082 4.49377 4.5422 4.49653 4.47609C4.49929 4.40999 4.51515 4.34508 4.54318 4.28515C4.57121 4.22521 4.61085 4.17144 4.65982 4.12694C4.70879 4.08244 4.7661 4.0481 4.82843 4.02591C4.89077 4.00373 4.95688 3.99413 5.02295 3.99769C5.08902 4.00124 5.15373 4.01787 5.21333 4.04662C5.27292 4.07537 5.32622 4.11566 5.37013 4.16515L15.3701 15.1652ZM13.2364 11.3314C13.2828 11.3825 13.3393 11.4234 13.4024 11.4516C13.4654 11.4798 13.5336 11.4946 13.6026 11.4952H13.6064C13.675 11.4951 13.7429 11.4809 13.8058 11.4535C13.8687 11.4261 13.9253 11.386 13.972 11.3358L15.3658 9.84265C15.4219 9.78273 15.4624 9.70989 15.4838 9.63058C15.5051 9.55127 15.5065 9.46793 15.488 9.38793C15.4695 9.30792 15.4315 9.23371 15.3775 9.17186C15.3235 9.11 15.255 9.06241 15.1783 9.03328L11.5739 7.68265L12.4901 3.09953C12.5109 2.99304 12.4964 2.88269 12.449 2.78514C12.4015 2.68759 12.3236 2.60812 12.227 2.55873C12.1304 2.50934 12.0204 2.4927 11.9135 2.51134C11.8066 2.52997 11.7087 2.58286 11.6345 2.66203L8.767 5.73265C8.68137 5.82442 8.63338 5.94504 8.63257 6.07055C8.63176 6.19606 8.67819 6.31729 8.76263 6.41015L13.2364 11.3314Z" fill="white"></path></svg>
                                    </div>

                                    <div className="text-[#c7c9d9] hover:text-[#faad14]">
                                        <svg width="21" height="20" viewBox="0 0 21 20" xmlns="http://www.w3.org/2000/svg"><circle cx="10.333" cy="10.0039" r="10" fill="currentColor"></circle><path d="M16.2025 8.6917L13.2783 11.6257C13.5277 12.3186 13.6311 13.479 12.557 14.9069C12.4819 15.0073 12.386 15.0903 12.2758 15.1503C12.1657 15.2102 12.0439 15.2457 11.9188 15.2542C11.898 15.2542 11.8778 15.2542 11.857 15.2542C11.742 15.2542 11.6282 15.2316 11.5219 15.1876C11.4157 15.1436 11.3192 15.079 11.2379 14.9977L8.59871 12.3558L6.26738 14.6887C6.18529 14.7708 6.07395 14.8169 5.95785 14.8169C5.84175 14.8169 5.73041 14.7708 5.64832 14.6887C5.56623 14.6066 5.52011 14.4953 5.52011 14.3792C5.52011 14.2631 5.56623 14.1518 5.64832 14.0697L7.98129 11.7383L5.33988 9.09694C5.25306 9.01001 5.18552 8.90577 5.14165 8.791C5.09779 8.67624 5.07858 8.55352 5.08529 8.43084C5.092 8.30816 5.12446 8.18827 5.18058 8.07897C5.23669 7.96967 5.31519 7.87342 5.41097 7.79647C6.80113 6.67483 8.13168 6.89522 8.7141 7.07788L11.6453 4.13624C11.7266 4.05496 11.8231 3.99049 11.9292 3.94651C12.0354 3.90252 12.1492 3.87988 12.2641 3.87988C12.3791 3.87988 12.4929 3.90252 12.599 3.94651C12.7052 3.99049 12.8017 4.05496 12.8829 4.13624L16.2019 7.45467C16.366 7.61868 16.4582 7.84111 16.4583 8.07307C16.4584 8.30504 16.3664 8.52755 16.2025 8.6917Z" fill="white"></path></svg>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3">
                                    <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 flex-shrink-0">
                                        <img
                                            src={conn.avatar_url || getChannelImage(conn.channel_type_code)}
                                            alt={conn.display_name}
                                            className="rounded-md object-contain w-full h-full"
                                            onError={(e) => {
                                                const target = e.currentTarget;
                                                const fallback = getChannelImage(conn.channel_type_code);
                                                if (target.src !== fallback) {
                                                    target.src = fallback;
                                                }
                                            }}
                                        />
                                    </div>

                                    <div>
                                        <p className="font-medium text-gray-800 dark:text-gray-100 text-sm">
                                            {conn.display_name}
                                        </p>

                                        <div className='flex justify-start items-center gap-2 text-[12px] mt-1 t-text-white'>
                                            <img src={getChannelImage(conn.channel_type_code)} alt={conn.channel_type_code} className="w-[14px] h-[14px] rounded-sm object-cover" />
                                            {conn.external_id}
                                        </div>

                                        <div className='flex items-center gap-1 text-[12px] mt-1 text-[#22c55e]'>
                                            <TeamOutlined />
                                            <span className="font-medium">
                                                {(conn.total_channel_customers || 0).toLocaleString('vi-VN')}
                                            </span>
                                            <span className="text-gray-400">{t('chats.customers')}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}

                </div>
            </div>
        </Layout>
    );
}